import { useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from '../hooks/useTranslation';
import { usePagination } from '../hooks/usePagination';
import { useTableSort } from '../hooks/useTableSort';
import { useFetchPaginatedData } from '../hooks/useFetchPaginatedData';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { serializeParams } from '../utils/params';
import PaginationControls from '../components/PaginationControls';
import ErrorLoading from '../components/ErrorLoading';
import AutoRefreshControls from '../components/AutoRefreshControls';
import { formatDateTime } from '../utils/format';

const dataSource = 'now_goal';

function NowGoalData({ token, backendUrl, dateRange, setDateRange, error, setError, handleApplyDates }) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const pagination = usePagination('nowGoal.pageSize', 50);
  const { sortField, sortDirection, sortableHeader } = useTableSort('live_time', 'asc');

  const params = useMemo(() => ({
    start_date: dateRange.start_date,
    end_date: dateRange.end_date,
  }), [dateRange.start_date, dateRange.end_date]);

  const { data, loading: fetchLoading, refetch } = useFetchPaginatedData({
    dataUrl: `${backendUrl}/now_goal/dates`,
    countUrl: `${backendUrl}/now_goal/dates/count`,
    token,
    enabled: Boolean(token && dateRange.start_date && dateRange.end_date && !error),
    params,
    page: pagination.currentPage,
    pageSize: pagination.pageSize,
    setTotalCount: pagination.setTotalCount,
    setError,
  });

  const autoRefresh = useAutoRefresh(() => refetch(), { defaultInterval: 60, defaultEnabled: true });

  const [csvLoading, setCsvLoading] = useState(false);
  const loading = fetchLoading || csvLoading;

  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value);
    pagination.resetPage();
  }, [pagination]);

  const handleDateChange = useCallback((e) => {
    const { name, value } = e.target;
    setDateRange((prev) => ({ ...prev, [name]: value }));
  }, [setDateRange]);

  const handleApplyClick = useCallback(() => {
    pagination.resetPage();
    setError(null);
    handleApplyDates(dataSource);
  }, [pagination, setError, handleApplyDates]);

  const { filteredAndSortedData, pagedData } = useMemo(() => {
    const dataToFilter = Array.isArray(data) ? data : [];
    const normalizedQuery = searchQuery.toLowerCase().trim();

    const filteredData = normalizedQuery
      ? dataToFilter.filter((item) =>
          String(item.league_name).toLowerCase().includes(normalizedQuery) ||
          String(item.home_team).toLowerCase().includes(normalizedQuery) ||
          String(item.away_team).toLowerCase().includes(normalizedQuery)
        )
      : dataToFilter;

    const sorted = [...filteredData].sort((a, b) => {
      if (!sortField) return 0;
      const aValue = a[sortField] ? new Date(a[sortField]).getTime() : 0;
      const bValue = b[sortField] ? new Date(b[sortField]).getTime() : 0;
      if (isNaN(aValue) || isNaN(bValue)) {
        const aStr = String(a[sortField] ?? '').toLowerCase();
        const bStr = String(b[sortField] ?? '').toLowerCase();
        if (aStr < bStr) return sortDirection === 'asc' ? -1 : 1;
        if (aStr > bStr) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      }
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    });

    return { filteredAndSortedData: sorted, pagedData: sorted };
  }, [data, searchQuery, sortField, sortDirection]);

  const downloadCSV = async () => {
    setCsvLoading(true);
    try {
      const response = await axios.get(`${backendUrl}/now_goal/dates`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          start_date: dateRange.start_date,
          end_date: dateRange.end_date,
          skip: 0,
          limit: -1,
        },
        paramsSerializer: serializeParams,
      });
      const allEventData = Array.isArray(response.data.data) ? response.data.data : [];

      const normalizedQuery = searchQuery.toLowerCase().trim();
      const filteredData = normalizedQuery
        ? allEventData.filter((item) =>
            String(item.league_name).toLowerCase().includes(normalizedQuery) ||
            String(item.home_team).toLowerCase().includes(normalizedQuery) ||
            String(item.away_team).toLowerCase().includes(normalizedQuery)
          )
        : allEventData;

      const finalSortedData = [...filteredData].sort((a, b) => {
        if (!sortField) return 0;
        const aValue = a[sortField] ? new Date(a[sortField]).getTime() : 0;
        const bValue = b[sortField] ? new Date(b[sortField]).getTime() : 0;
        if (isNaN(aValue) || isNaN(bValue)) {
          const aStr = String(a[sortField] ?? '').toLowerCase();
          const bStr = String(b[sortField] ?? '').toLowerCase();
          if (aStr < bStr) return sortDirection === 'asc' ? -1 : 1;
          if (aStr > bStr) return sortDirection === 'asc' ? 1 : -1;
          return 0;
        }
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      });

      const headers = [
        t('nowGoal.liveTime'), t('nowGoal.leagueName'), t('nowGoal.homeTeam'),
        t('nowGoal.currentScore'), t('nowGoal.awayTeam'), t('nowGoal.overGoal'),
        t('nowGoal.fullScore'), t('nowGoal.totalScore'), t('nowGoal.underOdds'),
        t('common.createdAt'),
      ];

      const rows = finalSortedData.map((item) => [
        item.live_time || '',
        item.league_name || '',
        item.home_team || '',
        item.current_score || '',
        item.away_team || '',
        item.over_goal || '',
        item.full_score || '',
        item.total_score || '',
        item.under_odds || '',
        formatDateTime(item.created_at),
      ]);

      const csvContent = [
        '﻿' + headers.join(','),
        ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `${dataSource}_data_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('下載 CSV 錯誤:', err);
      setError(t('common.csvDownloadError'));
    } finally {
      setCsvLoading(false);
    }
  };

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center my-4">
        <h2>{t('nowGoal.title')}</h2>
        <div className="d-flex align-items-center gap-3">
          <AutoRefreshControls {...autoRefresh} />
          <button className="btn btn-success" onClick={downloadCSV} disabled={csvLoading}>
            {t('common.downloadCsv')}
          </button>
        </div>
      </div>

      <div className="mb-3">
        <div className="row g-2 align-items-center">
          <div className="col-auto">
            <label htmlFor="search" className="form-label">{t('common.search')}</label>
            <input
              type="text"
              id="search"
              value={searchQuery}
              onChange={handleSearchChange}
              className="form-control"
              placeholder={t('nowGoal.searchPlaceholder')}
            />
          </div>
          <div className="col-auto">
            <label htmlFor="start_date" className="form-label">{t('common.startDate')}</label>
            <input
              type="date"
              id="start_date"
              name="start_date"
              value={dateRange.start_date}
              onChange={handleDateChange}
              className="form-control"
            />
          </div>
          <div className="col-auto">
            <label htmlFor="end_date" className="form-label">{t('common.endDate')}</label>
            <input
              type="date"
              id="end_date"
              name="end_date"
              value={dateRange.end_date}
              onChange={handleDateChange}
              className="form-control"
            />
          </div>
          <div className="col-auto align-self-end">
            <button className="btn btn-primary" onClick={handleApplyClick}>
              {t('common.apply')}
            </button>
          </div>
        </div>

        <p className="text-muted small mb-3">
          {t('common.dateRange')}: {dateRange.start_date} {t('common.to')} {dateRange.end_date}
          {pagination.totalCount > 0 && <span> | {t('common.totalCount')}: {pagination.totalCount} | {t('common.filteredCount')}: {filteredAndSortedData.length}</span>}
        </p>

        <PaginationControls {...pagination} />
      </div>

      <ErrorLoading error={error} loading={loading} />

      {pagedData && Array.isArray(pagedData) && !loading ? (
        <div className="table-responsive">
          <table className="table table-striped table-bordered table-hover">
            <thead>
              <tr>
                {sortableHeader(t('nowGoal.liveTime'), 'live_time')}
                <th>{t('nowGoal.leagueName')}</th>
                <th>{t('nowGoal.homeTeam')}</th>
                <th>{t('nowGoal.currentScore')}</th>
                <th>{t('nowGoal.awayTeam')}</th>
                <th>{t('nowGoal.overGoal')}</th>
                <th>{t('nowGoal.fullScore')}</th>
                <th>{t('nowGoal.totalScore')}</th>
                <th>{t('nowGoal.underOdds')}</th>
                {sortableHeader(t('common.createdAt'), 'created_at')}
                <th style={{ width: '40px' }}>URL</th>
              </tr>
            </thead>
            <tbody>
              {pagedData.map((item, index) => (
                <tr key={index}>
                  <td>{item.live_time}</td>
                  <td>{item.league_name}</td>
                  <td>{item.home_team}</td>
                  <td>{item.current_score}</td>
                  <td>{item.away_team}</td>
                  <td>{item.over_goal}</td>
                  <td>{item.full_score || '-'}</td>
                  <td>{item.total_score || '-'}</td>
                  <td>{item.under_odds}</td>
                  <td>{formatDateTime(item.created_at)}</td>
                  <td className="text-center">
                    {item.url && (
                      <a href={item.url} target="_blank" rel="noreferrer" title={item.url} aria-label={item.url}>
                        <i className="bi bi-box-arrow-up-right" aria-hidden="true"></i>
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        !loading && <p className="text-muted">{t('common.noData')}</p>
      )}
    </div>
  );
}

export default NowGoalData;
